import React, { useMemo, useState } from 'react';
import { entityLabel } from './entityLabels';

// Completeness over time: an Overall line plus one line per entity, points = runs that
// baked a snapshot. Hand-rolled SVG to match the rest of the dashboard (no chart lib).
// Colours follow the dataviz skill; keep them distinct and theme-legible.
const SERIES_COLORS = {
  overall: '#00897B', // teal, the headline
  companies: '#3B6FE0',
  people: '#C15A1E',
  jobs: '#8E5BD0',
};
const colorFor = (key, i) => SERIES_COLORS[key] || ['#3B6FE0', '#C15A1E', '#8E5BD0', '#D0405B'][i % 4];
const fmtDate = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

export default function CompletenessTrend({ runs }) {
  const points = useMemo(
    () => (runs || [])
      .filter((r) => r.completeness)
      .map((r) => ({ at: new Date(r.completed_at).getTime(), iso: r.completed_at, c: r.completeness }))
      .sort((a, b) => a.at - b.at),
    [runs]
  );

  const series = useMemo(() => {
    if (!points.length) return [];
    const entityKeys = [];
    points.forEach((p) => p.c.entities.forEach((e) => { if (!entityKeys.includes(e.entity)) entityKeys.push(e.entity); }));
    const overall = { key: 'overall', label: 'Overall', values: points.map((p) => p.c.overall_pct) };
    const perEntity = entityKeys.map((k) => ({
      key: k,
      label: entityLabel(k),
      values: points.map((p) => (p.c.entities.find((e) => e.entity === k)?.pct ?? null)),
    }));
    const all = [overall, ...perEntity];
    return all.map((s, i) => ({ ...s, color: colorFor(s.key, i) }));
  }, [points]);

  const [hidden, setHidden] = useState(() => new Set());
  const toggle = (key) => setHidden((h) => {
    const n = new Set(h);
    n.has(key) ? n.delete(key) : n.add(key);
    return n;
  });

  if (!points.length) {
    return <p className="text-sm text-gray-500 py-6 text-center">No completeness history yet — it appears after your first import.</p>;
  }

  const W = 720, H = 200, padL = 34, padR = 12, padT = 12, padB = 24;
  const n = points.length;
  const x = (i) => padL + (n === 1 ? (W - padL - padR) / 2 : (i * (W - padL - padR)) / (n - 1));
  const y = (pct) => padT + (H - padT - padB) * (1 - Math.max(0, Math.min(100, pct)) / 100);
  const pathFor = (vals) => vals
    .map((v, i) => (v == null ? null : `${i === 0 || vals[i - 1] == null ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`))
    .filter(Boolean)
    .join(' ');

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Completeness over time" className="w-full">
        {[0, 25, 50, 75, 100].map((g) => (
          <g key={g}>
            <line x1={padL} x2={W - padR} y1={y(g)} y2={y(g)} stroke="#eee" />
            <text x={padL - 6} y={y(g) + 3} textAnchor="end" fontSize="9" fill="#9ca3af">{g}%</text>
          </g>
        ))}
        {series.filter((s) => !hidden.has(s.key)).map((s) => (
          <g key={s.key} data-testid={`series-${s.key}`}>
            <path d={pathFor(s.values)} fill="none" stroke={s.color} strokeWidth="2" />
            {s.values.map((v, j) => v != null && (
              <circle key={j} cx={x(j)} cy={y(v)} r="2.5" fill={s.color} />
            ))}
          </g>
        ))}
        {points.map((p, i) => (
          <text key={i} x={x(i)} y={H - 8} textAnchor="middle" fontSize="9" fill="#9ca3af">{fmtDate(p.iso)}</text>
        ))}
      </svg>
      <div className="flex flex-wrap gap-3 mt-2">
        {series.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => toggle(s.key)}
            aria-pressed={!hidden.has(s.key)}
            className={`inline-flex items-center gap-1.5 text-xs ${hidden.has(s.key) ? 'text-gray-400' : 'text-gray-700'}`}
          >
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: hidden.has(s.key) ? '#d1d5db' : s.color }} />
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}
